import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { FiPlus, FiInbox, FiEdit2, FiX } from 'react-icons/fi';
import { BountyCard } from './BountyCard';
import { useMyBounties, cancelBounty } from '../../hooks/useBounties';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';

const STATUS_GROUPS = {
  active: ['open'],
  pending: ['claimed', 'in_review'],
  completed: ['completed', 'cancelled'],
};

export default function MyBounties() {
  const { currentUser } = useAuth();
  const { bounties, loading } = useMyBounties(currentUser?.uid);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [bountyToCancel, setBountyToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const toast = useToast();

  const grouped = {
    active: bounties.filter((b) => STATUS_GROUPS.active.includes(b.status)),
    pending: bounties.filter((b) => STATUS_GROUPS.pending.includes(b.status)),
    completed: bounties.filter((b) => STATUS_GROUPS.completed.includes(b.status)),
  };

  const handleCancelClick = (bounty) => {
    setBountyToCancel(bounty);
    onOpen();
  };

  const confirmCancel = async () => {
    if (!bountyToCancel) return;
    setCancelling(true);
    try {
      await cancelBounty(bountyToCancel.id);
      toast({
        title: 'Task cancelled',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      setBountyToCancel(null);
    } catch (err) {
      toast({
        title: 'Error cancelling task',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Container maxW="7xl" py={6} px={0}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">My Tasks</Heading>
            <Text color="gray.600" mt={1}>
              {loading ? '' : `${bounties.length} total tasks posted`}
            </Text>
          </Box>
          <Button
            as={Link}
            to="/tasks/create"
            colorScheme="orange"
            leftIcon={<Icon as={FiPlus} />}
          >
            Post New Task
          </Button>
        </HStack>

        {loading ? (
          <LoadingSpinner message="Loading your tasks..." />
        ) : bounties.length === 0 ? (
          <EmptyState
            title="No tasks yet"
            description="Post your first research task to get started"
            icon={FiInbox}
          />
        ) : (
          <Tabs colorScheme="orange">
            <TabList>
              <Tab>Active ({grouped.active.length})</Tab>
              <Tab>Pending ({grouped.pending.length})</Tab>
              <Tab>Completed ({grouped.completed.length})</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                {grouped.active.length === 0 ? (
                  <EmptyState title="No active tasks" icon={FiInbox} />
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5} mt={4}>
                    {grouped.active.map((bounty) => (
                      <BountyCard
                        key={bounty.id}
                        bounty={bounty}
                        actionButtons={
                          <HStack spacing={2} w="full">
                            <Button
                              as={Link}
                              to={`/tasks/${bounty.id}/edit`}
                              size="xs"
                              flex="1"
                              leftIcon={<Icon as={FiEdit2} />}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Edit
                            </Button>
                            <Button
                              size="xs"
                              flex="1"
                              colorScheme="red"
                              variant="outline"
                              leftIcon={<Icon as={FiX} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleCancelClick(bounty);
                              }}
                            >
                              Cancel
                            </Button>
                          </HStack>
                        }
                      />
                    ))}
                  </SimpleGrid>
                )}
              </TabPanel>
              <TabPanel px={0}>
                {grouped.pending.length === 0 ? (
                  <EmptyState title="No pending tasks" icon={FiInbox} />
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5} mt={4}>
                    {grouped.pending.map((bounty) => (
                      <BountyCard key={bounty.id} bounty={bounty} to={`/tasks/${bounty.id}/review`} />
                    ))}
                  </SimpleGrid>
                )}
              </TabPanel>
              <TabPanel px={0}>
                {grouped.completed.length === 0 ? (
                  <EmptyState title="No completed tasks" icon={FiInbox} />
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5} mt={4}>
                    {grouped.completed.map((bounty) => (
                      <BountyCard key={bounty.id} bounty={bounty} />
                    ))}
                  </SimpleGrid>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </VStack>

      {/* Cancel confirmation modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cancel Task</ModalHeader>
          <ModalBody>
            <Text>Are you sure you want to cancel this task? This cannot be undone.</Text>
            {bountyToCancel?.status === 'in_review' && (
              <Text mt={3} fontSize="sm" color="orange.600">
                Note: This task has a pending submission which will be automatically rejected.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Keep Task
            </Button>
            <Button colorScheme="red" onClick={confirmCancel} isLoading={cancelling}>
              Cancel Task
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}
